import React, { useState, useRef, useEffect } from 'react';
import { useApp } from '../context/AppContext';

function QueryScreenV2() {
  const { state, dispatch } = useApp();
  const [query, setQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [chatHistory, setChatHistory] = useState([
    {
      type: 'system',
      content: '🧠 Welcome to Brainstorming Agent v2! I use enhanced context and your selected user as the actor for actions and notes.',
      timestamp: new Date().toLocaleTimeString(),
      isWelcome: true
    }
  ]);
  const chatEndRef = useRef(null);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  useEffect(() => {
    const id = `brainstorm_v2_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setSessionId(id);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim() || isSubmitting) return;

    setIsSubmitting(true);
    const currentQuery = query;

    const userMessage = {
      type: 'user',
      content: currentQuery,
      timestamp: new Date().toLocaleTimeString()
    };
    const loadingMessage = {
      type: 'ai',
      content: 'Thinking (v2)...',
      timestamp: new Date().toLocaleTimeString(),
      isLoading: true
    };
    setChatHistory((prev) => [...prev, userMessage, loadingMessage]);
    setQuery('');

    try {
      const requestBody = {
        session_id: sessionId,
        user_id: state?.selectedUser || 'anonymous_user',
        chat_input: currentQuery
      };

      const response = await fetch('https://langraphdev-development.up.railway.app/api/mission/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        mode: 'cors',
        body: JSON.stringify(requestBody)
      });

      const responseText = await response.text();
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (err) {
        responseData = { success: false, error: 'Invalid JSON', raw: responseText.slice(0, 500) };
      }

      setChatHistory((prev) => prev.filter((m) => !m.isLoading));

      if (response.ok && responseData) {
        const text = responseData.response || responseData.message || JSON.stringify(responseData, null, 2);
        // Capture draft_json if present
        if (responseData.draft_json) {
          setDraft(responseData.draft_json);
        }
        setChatHistory((prev) => [
          ...prev,
          {
            type: 'ai',
            content: text,
            timestamp: new Date().toLocaleTimeString(),
            isSuccess: true
          }
        ]);
      } else {
        setChatHistory((prev) => [
          ...prev,
          { type: 'ai', content: `❌ v2 request failed: ${responseData?.error || response.statusText}`, timestamp: new Date().toLocaleTimeString(), isError: true }
        ]);
      }
    } catch (err) {
      setChatHistory((prev) => [
        ...prev.filter((m) => !m.isLoading),
        { type: 'ai', content: `❌ Network error: ${err.message}`, timestamp: new Date().toLocaleTimeString(), isError: true }
      ]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const goBackToDashboard = () => {
    dispatch({ type: 'SET_SCREEN', payload: 'dashboard' });
  };

  return (
    <div className="bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-6xl mx-auto h-[80vh] flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-white">Brainstorming Agent v2</h1>
        <button onClick={goBackToDashboard} className="text-sm text-gray-400 hover:text-white">← Back to Dashboard</button>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-4 min-h-0">
        {/* Chat Area */}
        <div className="lg:col-span-2 bg-gray-900/50 rounded-lg p-4 overflow-y-auto space-y-3 min-h-0 h-full">
          {chatHistory.map((m, i) => (
            <div key={i} className={`flex ${m.type === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-lg p-3 ${m.type === 'user' ? 'bg-gray-700 text-white border border-gray-600' : m.isError ? 'bg-red-900/50 text-red-200 border border-red-500' : 'bg-gray-700 text-gray-200 border border-gray-600'}`}>
                {m.isLoading ? (<div className="flex items-center gap-2"><div className="spinner w-4 h-4"></div>{m.content}</div>) : m.content}
                <div className="text-xs opacity-70 mt-1">{m.timestamp}</div>
              </div>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>

        {/* Draft JSON Form */}
        <div className="bg-gray-900/50 rounded-lg p-4 overflow-y-auto min-h-0 h-full">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-white">Draft (v2)</h2>
            {!draft && <span className="text-xs text-gray-400">waiting for first response…</span>}
          </div>
          {draft && (
            <div className="space-y-3 text-sm">
              <Field label="session_id" value={draft.session_id} />
              <Field label="mission_id" value={draft.mission_id} />
              <Field label="title" value={draft.title} />
              <Textarea label="description" value={draft.description} />
              <Field label="mission_type" value={draft.mission_type} />
              <Field label="lead_by" value={draft.assigned_to} />
              <Field label="initiated_by" value={draft.created_by} />
              <Field label="duration" value={draft.duration} />
              <Field label="difficulty" value={draft.difficulty} />
              <Field label="points" value={draft.points} />
              <Chips label="tags" items={draft.tags} />
              <Chips label="requirements" items={draft.requirements} />
              <Chips label="deliverables" items={draft.deliverables} />
              <Field label="deadline" value={draft.deadline} />
              <Field label="status" value={draft.status} />
              <Field label="created_at" value={draft.created_at} />
              <Field label="updated_at" value={draft.updated_at} />
              <Chips label="labels" items={draft.labels} />
              <Chips label="contributors" items={draft.contributors} />
              <Chips label="skills_req" items={draft.skills_req} />
              <Chips label="tools_req" items={draft.tools_req} />
              <Chips label="constraints" items={draft.constraints} />
              <Chips label="context_artifacts" items={draft.context_artifacts} />
              {/* <Field label="lead_by" value={draft.lead_by} /> */}
              <Field label="deliver_to" value={draft.deliver_to} />
              {Array.isArray(draft.missing) && draft.missing.length > 0 && (
                <Chips label="missing" items={draft.missing} highlight />
              )}
              {Array.isArray(draft.disambiguation) && draft.disambiguation.length > 0 && (
                <Chips label="disambiguation" items={draft.disambiguation} />
              )}
              <Textarea label="suggest_next" value={draft.suggest_next} />
              {draft.linked_mission && Object.keys(draft.linked_mission).length > 0 && (
                <div>
                  <label className="text-xs text-gray-400">linked_mission</label>
                  <div className="mt-1 bg-gray-800 border border-gray-700 rounded p-2 text-gray-200 text-xs">
                    {JSON.stringify(draft.linked_mission, null, 2)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Ask v2 to plan, prioritize, assign…"
          className="flex-1 bg-gray-700 border border-gray-600 rounded-lg p-3 text-sm"
          disabled={isSubmitting}
        />
        <button type="submit" disabled={isSubmitting || !query.trim()} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg disabled:opacity-50 flex items-center gap-2 border border-gray-600">
          {isSubmitting ? (<><div className="spinner w-4 h-4"></div>Send</>) : (<>Send</>)}
        </button>
      </form>
    </div>
  );
}

export default QueryScreenV2;

// Readonly field components for clean rendering
function Field({ label, value }) {
  return (
    <div>
      <label className="text-xs text-gray-400">{label}</label>
      <input
        className="w-full mt-1 bg-gray-800 border border-gray-700 rounded p-2 text-gray-200"
        value={value ?? ''}
        onChange={() => {}}
        readOnly
      />
    </div>
  );
}

function Textarea({ label, value }) {
  return (
    <div>
      <label className="text-xs text-gray-400">{label}</label>
      <textarea
        className="w-full mt-1 bg-gray-800 border border-gray-700 rounded p-2 text-gray-200"
        rows={3}
        value={value ?? ''}
        onChange={() => {}}
        readOnly
      />
    </div>
  );
}

function Chips({ label, items, highlight }) {
  const list = Array.isArray(items) ? items : [];
  return (
    <div>
      <label className="text-xs text-gray-400">{label}</label>
      <div className="mt-1 flex flex-wrap gap-2">
        {list.length === 0 && <span className="text-xs text-gray-500">—</span>}
        {list.map((it, idx) => (
          <span
            key={idx}
            className={`text-xs px-2 py-1 rounded border ${highlight ? 'bg-yellow-900/40 border-yellow-600 text-yellow-200' : 'bg-gray-800 border-gray-700 text-gray-200'}`}
          >
            {it}
          </span>
        ))}
      </div>
    </div>
  );
}


